import React from 'react';
import { MapPin, Briefcase, DollarSign, Bookmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const JobCard = ({ job, onSave, isSaved }) => {
  const navigate = useNavigate();

  const formatSalary = () => {
    const min = job.salary_min || 0;
    const max = job.salary_max || 0;
    
    // No salary provided
    if (min === 0 && max === 0) {
      return 'Negotiable';
    }
    
    const salary = max || min;
    
    // Hourly rate (value < 1000)
    if (salary < 1000) {
      // Format with 2 decimal places, remove trailing zeros
      const formatHourly = (val) => {
        const formatted = val.toFixed(2);
        return formatted.endsWith('.00') ? val.toString() : formatted;
      };
      
      if (min > 0 && max > 0 && min !== max) {
        return `$${formatHourly(min)}-$${formatHourly(max)}/hr`;
      }
      return `$${formatHourly(salary)}/hr`;
    }
    
    // Annual salary
    if (min > 0 && max > 0 && min !== max) {
      return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k/yr`;
    }
    return `$${(salary / 1000).toFixed(0)}k/yr`;
  };

  return (
    <div
      className="bg-white rounded-xl p-6 border border-zinc-200 hover:shadow-lg hover:border-brand-blue/30 transition-all duration-200 cursor-pointer group"
      onClick={() => navigate(`/jobs/${job.id}`)}
      data-testid={`job-card-${job.id}`}
    >
      {/* Location */}
      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-2" data-testid="job-location">
        <MapPin className="w-4 h-4" strokeWidth={2} />
        <span>{job.location}</span>
      </div>

      {/* Job Title */}
      <h3 className="text-xl font-bold text-zinc-900 mb-2 group-hover:text-brand-blue transition-colors" data-testid="job-title">
        {job.title}
      </h3>

      {/* Salary */}
      <div className="flex items-center gap-2 mb-3 text-zinc-700">
        <span className="text-sm">Salary:</span>
        <span className="font-semibold" data-testid="job-salary">{formatSalary()}</span>
      </div>

      {/* Description Preview */}
      <p className="text-zinc-600 text-sm leading-relaxed line-clamp-4">
        {job.description && job.description.length > 250 
          ? `${job.description.substring(0, 250)}...` 
          : job.description}
      </p>

      {/* Footer: Job Type */}
      <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-zinc-100 text-sm text-zinc-500">
        <div className="flex items-center gap-1.5" data-testid="job-type">
          <Briefcase className="w-4 h-4" strokeWidth={2} />
          <span className="capitalize">{job.job_type}</span>
        </div>
      </div>
    </div>
  );
};